import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, ArrowRight, CalendarPlus, CheckCircle2, Trash2 } from "lucide-react";
import { Badge, Button, Loader } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import {
  type Lead, type Activity,
  addLeadActivity, convertLead, deleteLead, getLead, updateActivity, updateLead,
} from "../../../lib/clients";
import { earliestOutstandingAt } from "../../../lib/nextStep";
import { Btn, WebsiteLink } from "../../ui";
import { PhoneLink } from "../../telephony/PhoneLink";
import { ActivityList } from "../_shared/ActivityList";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { NextStepBadge, NextStepDialog } from "../_shared/NextStep";
import { TagChips } from "../_shared/formBits";
import { TAGS_MAX, TAG_MAX_LEN } from "../projects/projectFields";
import { LeadCalls } from "./LeadCalls";
import { LeadSource } from "./LeadSource";
import { LeadOrigin } from "./LeadOrigin";

/** One juhuklient: what we know, and everything that has happened to it. */
export function LeadDetail({ user: _user }: { user: StaffUser }) {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [lead, setLead] = useState<(Lead & { activities: Activity[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const [planning, setPlanning] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getLead(id).then((l) => setLead(l)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loader />;
  if (!lead) return <p className="text-muted-foreground">{t("leads.notFound")}</p>;

  return (
    <div className="space-y-4">
      <button onClick={() => nav("/leads")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronRight className="size-3.5 rotate-180" /> {t("leads.back")}
      </button>
      <div className="rounded-xl border border-border bg-background p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{lead.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{t(`leads.status.${lead.status}`)}</Badge>
              <LeadSource lead={lead} t={t} className="px-2 py-1 text-[11px]" />
              {lead.convertedClientId && (
                <Badge className="gap-1 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
                  <CheckCircle2 className="size-3" /> {t("leads.convertedBadge")}
                </Badge>
              )}
              {/* Read off the timeline this page already loaded rather than from
                  a list field it never receives - `nextStepAt` is computed by the
                  list route, and the detail fetches the activities themselves. */}
              {!lead.convertedClientId ? (
                <NextStepBadge
                  nextStepAt={earliestOutstandingAt(lead.activities)}
                  onPlan={() => setPlanning(true)}
                />
              ) : null}
            </div>
            <LeadOrigin lead={lead} t={t} />
          </div>
          {/* Both actions the record has, in the order they get used: the one
              that promotes it, then the one that ends it. */}
          <div className="flex shrink-0 items-center gap-2">
            {!lead.convertedClientId && (
              <Btn onClick={() => setPlanning(true)}>
                <CalendarPlus className="size-4" /> {t("nextStep.plan")}
              </Btn>
            )}
            {!lead.convertedClientId && (
              <Button size="sm" onClick={async () => {
                const res = await convertLead(lead.id);
                if (res.ok) nav(`/clients/${res.client.id}`);
              }}>
                <ArrowRight className="size-3.5" /> {t("leads.convert")}
              </Button>
            )}
            <Btn tone="danger" onClick={() => setConfirmDel(true)}>
              <Trash2 className="size-4" /> {t("common.delete")}
            </Btn>
          </div>
        </div>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          {lead.email && <div><span className="text-muted-foreground">{t("leads.email")}: </span>{lead.email}</div>}
          {lead.phone && (
            <div className="inline-flex items-center gap-1">
              <span className="text-muted-foreground">{t("leads.phone")}: </span>
              <PhoneLink
                phone={lead.phone}
                source="manual"
                companyName={lead.name}
                regCode={lead.regCode ?? undefined}
                leadId={lead.id}
                hideIcon
              />
            </div>
          )}
          {lead.regCode && <div><span className="text-muted-foreground">{t("leads.regCode")}: </span>{lead.regCode}</div>}
          {lead.vatNumber && <div><span className="text-muted-foreground">{t("leads.vat")}: </span>{lead.vatNumber}</div>}
          {lead.website && <div><span className="text-muted-foreground">{t("leads.website")}: </span><WebsiteLink value={lead.website} /></div>}
          {lead.address && <div><span className="text-muted-foreground">{t("leads.address")}: </span>{lead.address}</div>}
          {lead.valueCents != null && <div><span className="text-muted-foreground">{t("leads.value")}: </span>{(lead.valueCents / 100).toLocaleString("et-EE", { style: "currency", currency: "EUR" })}</div>}
        </div>
        {lead.notes && <p className="mt-3 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">{lead.notes}</p>}

        {/* The one editable field on this page, and the reason it is here rather
            than on the board card: a card is 240px wide, and a chip control on
            it would be a text box nobody can read what they typed into. Writing
            straight through on change, the same way the card's status dropdown
            does - there is no other pending edit on this page for a Save button
            to batch with. */}
        <div className="mt-4">
          <span className="crm-lab">{t("leads.tags")}</span>
          <TagChips
            tags={lead.tags}
            onChange={(next) => {
              // Local first so the chip appears under the cursor, then written.
              // A failed write leaves the optimistic chip on screen; the record
              // is reloaded on the next visit and nothing was destroyed, which
              // is the right trade for a label.
              setLead((prev) => (prev ? { ...prev, tags: next } : prev));
              void updateLead(lead.id, { tags: next });
            }}
            placeholder={t("leads.tagsPlaceholder")}
            maxLen={TAG_MAX_LEN}
            maxCount={TAGS_MAX}
            removeLabel={t("common.remove")}
          />
        </div>
      </div>

      {/* Above the activities, because it answers the question somebody opens
          this page to ask: has anybody rung them yet. */}
      <LeadCalls leadId={lead.id} />

      <div className="rounded-xl border border-border bg-background p-5">
        <h2 className="mb-3 text-sm font-semibold">{t("leads.activities")}</h2>
        <ActivityList
          rows={lead.activities}
          empty={t("leads.noActivities")}
          onDone={async (a) => {
            const done = new Date().toISOString();
            // Local first so the line clears under the cursor, then written.
            setLead((prev) =>
              prev
                ? { ...prev, activities: prev.activities.map((x) => (x.id === a.id ? { ...x, doneAt: done } : x)) }
                : prev,
            );
            await updateActivity(a.id, { doneAt: done });
          }}
        />
      </div>

      {planning ? (
        <NextStepDialog
          title={lead.name}
          onClose={() => setPlanning(false)}
          onSave={(body) => addLeadActivity(lead.id, body)}
          onSaved={(activity) => {
            setLead((prev) => (prev ? { ...prev, activities: [activity, ...prev.activities] } : prev));
            setPlanning(false);
          }}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("leads.deleteTitle")}
          body={t("leads.deleteConfirm", { name: lead.name })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await deleteLead(lead.id);
            nav("/leads");
          }}
          onClose={() => setConfirmDel(false)}
        />
      ) : null}
    </div>
  );
}
